import React, { useCallback, useEffect, useState } from "react";
import { fetchMedia } from "../../Helpers/notificationConsoleApi";

// A template's picture (Notification Centre redesign, R4): a file from Notification Centre > Media, and /
// or a media detail filled at send time ({carImage}). The detail wins when the message has it; the file
// is used otherwise. value: { mediaId, detail, alt, after? } or null.
//   kind      image | video | document (what the file must be)
//   use       push | email | whatsapp (which crop is shown)
//   details   the media details this message can fill ([{ name, label, kind }])
//   needsAlt  email: alt text is asked for
//   needsFile a WhatsApp header always needs its file (Meta reviews it; it is the fallback)
const KIND_NAME = { image: "picture", video: "video", document: "PDF" };

export const MediaPicker = ({ kind = "image", use = "push", onPick, onClose }) => {
    const [items, setItems] = useState(null);
    const [q, setQ] = useState("");
    const load = useCallback(async () => {
        const result = await fetchMedia({ kind, ...(q ? { q } : {}) });
        setItems(result.ok ? result.data.items || [] : []);
    }, [kind, q]);
    useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
    return (
        <div className="te-picker" role="dialog" aria-label={`Choose a ${KIND_NAME[kind]}`}>
            <div className="te-picker-head">
                <strong>Choose a {KIND_NAME[kind]} from Media</strong>
                <button type="button" className="kit-icon-btn" onClick={onClose} aria-label="Close">×</button>
            </div>
            <input type="search" className="form-control form-control-sm mb-2" placeholder="Search Media" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search Media" />
            {items === null ? <div className="kit-sub">Loading...</div> : items.length === 0 ? (
                <div className="kit-sub">No {KIND_NAME[kind]}s in Media yet. Upload one in Notification Centre &gt; Media.</div>
            ) : (
                <div className="te-picker-grid">
                    {items.map((a) => {
                        const fit = a.fit?.[use];
                        const usable = !fit || fit.ok;
                        return (
                            <button key={a.mediaId} type="button" className={`te-picker-item ${usable ? "" : "is-unfit"}`} disabled={!usable} title={fit?.reason || a.name} onClick={() => onPick(a)}>
                                {a.kind === "image" ? <img src={a.links?.[use] || a.url} alt="" /> : <span className="te-picker-file">{a.kind === "video" ? "▶" : "PDF"}</span>}
                                <span className="te-picker-name">{a.name}</span>
                                {fit?.reason ? <span className="kit-sub">{fit.reason}</span> : null}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const PictureField = ({ label, value, onChange, kind = "image", use = "push", details = [], needsAlt = false, needsFile = false, disabled, help }) => {
    const [picking, setPicking] = useState(false);
    const [chosen, setChosen] = useState(null); // the picked asset, for its thumbnail
    const pic = value || {};
    const pictureDetails = kind === "image" ? details.filter((d) => d.kind === "image") : [];
    const set = (patch) => {
        const next = { ...pic, ...patch };
        onChange(next.mediaId || next.detail ? next : null);
    };
    // Show the saved file's thumbnail without a click: look it up once.
    useEffect(() => {
        let gone = false;
        if (!pic.mediaId || chosen?.mediaId === pic.mediaId) return undefined;
        (async () => {
            const result = await fetchMedia({ kind });
            const found = (result.data?.items || []).find((a) => a.mediaId === pic.mediaId);
            if (!gone) setChosen(found || { mediaId: pic.mediaId, name: "(not in Media any more)" });
        })();
        return () => { gone = true; };
    }, [pic.mediaId, kind]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="te-picture">
            <div className="te-picture-head">
                <span className="form-label small mb-0">{label}</span>
                {value && !disabled ? <button type="button" className="btn btn-link btn-sm p-0" onClick={() => { setChosen(null); onChange(null); }}>Remove</button> : null}
            </div>
            <div className="te-picture-body">
                <div className="te-picture-thumb">
                    {pic.mediaId && chosen?.kind === "image" ? <img src={chosen.links?.[use] || chosen.url} alt="" /> : pic.mediaId ? <span>{kind === "video" ? "▶" : kind === "document" ? "PDF" : "…"}</span> : <span className="kit-sub">None</span>}
                </div>
                <div className="te-picture-info">
                    <div className="kit-sub">{pic.mediaId ? (chosen?.name || pic.mediaId) : needsFile ? `No ${KIND_NAME[kind]} chosen yet` : `No fixed ${KIND_NAME[kind]}`}</div>
                    <button type="button" className="btn btn-outline-primary btn-sm" disabled={disabled} onClick={() => setPicking(true)}>{pic.mediaId ? "Change" : `Choose a ${KIND_NAME[kind]}`}</button>
                </div>
            </div>
            {pictureDetails.length ? (
                <label className="te-picture-detail">
                    <input type="checkbox" disabled={disabled} checked={!!pic.detail} onChange={(e) => set({ detail: e.target.checked ? pictureDetails[0].name : "" })} />
                    <span>Use {pictureDetails.length === 1 ? `the ${pictureDetails[0].label.toLowerCase()}` : "a picture from the message"} when the message has one</span>
                </label>
            ) : null}
            {pic.detail && pictureDetails.length > 1 ? (
                <select className="form-select form-select-sm mt-1" value={pic.detail} disabled={disabled} onChange={(e) => set({ detail: e.target.value })} aria-label="Which picture">
                    {pictureDetails.map((d) => <option key={d.name} value={d.name}>{d.label}</option>)}
                </select>
            ) : null}
            {pic.detail ? <div className="kit-sub">{pic.mediaId ? "Otherwise the file above is sent." : needsFile ? "Choose a file too: it is sent when the message has none." : "Otherwise there is no picture."}</div> : null}
            {needsAlt && value ? (
                <input className="form-control form-control-sm mt-2" placeholder="What the picture shows (alt text, required)" maxLength={200} value={pic.alt || ""} disabled={disabled} onChange={(e) => set({ alt: e.target.value })} aria-label={`${label}: alt text`} />
            ) : null}
            {help ? <div className="kit-sub mt-1">{help}</div> : null}
            {picking ? <MediaPicker kind={kind} use={use} onClose={() => setPicking(false)} onPick={(a) => { setChosen(a); set({ mediaId: a.mediaId }); setPicking(false); }} /> : null}
        </div>
    );
};

export default PictureField;
